import React, { useEffect, useRef } from "react";
import { useController, useFormContext } from "react-hook-form";
import Flatpickr from "flatpickr";
import { Polish } from "flatpickr/dist/l10n/pl.js";
import { InputLabel } from "../InputLabel/InputLabel";
import { InputErrorMessage } from "../InputErrorMessage/InputErrorMessage";
import { formatDate } from "../../../utils/dateUtils";
import { FaRegCalendar } from "react-icons/fa";

export type DatepickerMode = "single" | "multiple" | "range" | "time";
interface DatepickerProps {
  name: string;
  required?: boolean;
  label?: string;
  width?: string;
  disable?: {
    from: Date;
    to: Date;
  }[];
  disabled?: boolean;
  mode?: DatepickerMode;
  onChange: (event: Date) => void;
  value?: any;
}

export const Datepicker = ({
  label,
  required,
  name,
  value,
  onChange,
  mode = "single",
  disabled,
  disable,
  width = "w-full",
}: DatepickerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  value = typeof value === "string" ? new Date(value) : value;
  value?.setHours(12);
  const options: Flatpickr.Options.Options = {
    mode,
    static: true,
    monthSelectorType: "static" as const,
    dateFormat: "M j, Y",
    locale: {
      ...Polish,
    },
    disable: disable ?? [],
    defaultDate: formatDate(value),
    prevArrow:
      '<svg class="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M5.4 10.8l1.4-1.4-4-4 4-4L5.4 0 0 5.4z" /></svg>',
    nextArrow:
      '<svg class="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M1.4 10.8L0 9.4l4-4-4-4L1.4 0l5.4 5.4z" /></svg>',
    onReady: (selectedDates: Date[], dateStr: string, instance: any) => {
      instance.element.value = dateStr.replace("to", "-");
      const customClass = ""; //align ? align : "";
      instance.calendarContainer.classList.add(`flatpickr-${customClass}`);
    },

    onChange: (selectedDates: Date[], dateStr: string, instance: any) => {
      const ne = selectedDates[0];
      ne.setHours(12);
      onChange(ne);
      instance.element.value = dateStr.replace("to", "-");
    },
  };

  useEffect(() => {
    if (inputRef.current) {
      // Initialize Flatpickr
      Flatpickr(inputRef.current, {
        ...options,
      });
    }
    return () => {
      if (inputRef.current) {
        // Destroy Flatpickr instance when component is unmounted
        //@ts-ignore
        inputRef.current._flatpickr.destroy();
      }
    };
  }, [inputRef, options]);

  const additionalInputClasses = disabled ? classNames.disabled : "";
  return (
    <div>
      <InputLabel label={label} required={required} name={name} />
      <div className="relative w-full">
        <input
          className={`form-input pl-9 dark:bg-slate-800 text-slate-500 hover:text-slate-600 dark:text-slate-300
           dark:hover:text-slate-200 font-medium ${width} ${additionalInputClasses}`}
          ref={inputRef}
          disabled={disabled}
          defaultValue={formatDate(value ?? "")}
          value={formatDate(value ?? "")}
          onChange={() => {}}
          type="text"
          data-input
          placeholder="Wybierz datę"
        />
        <div className="absolute inset-0 right-auto flex items-center pointer-events-none">
          <FaRegCalendar className="text-slate-500 dark:text-slate-400 ml-3" />
        </div>
      </div>
    </div>
  );
};

export interface PaziaDatepickerProps {
  name: string;
  required?: boolean;
  label?: string;
  width?: string;
  disable?: {
    from: Date;
    to: Date;
  }[];
  onChange?: (date: Date) => void;
  className?: string;
  disabled?: boolean;
}

export const PaziaDatepicker = ({
  name,
  required,
  label,
  width,
  onChange,
  disable,
  disabled,
  className = "",
}: PaziaDatepickerProps) => {
  const { control } = useFormContext();

  const { field } = useController({ name, control });

  const onChangeHandler = (date: Date) => {
    field.onChange(date);
    if (onChange) onChange(date);
  };

  return (
    <div className={className}>
      <Datepicker
        label={label}
        width={width}
        disable={disable}
        onChange={onChangeHandler}
        required={required}
        disabled={disabled}
        value={field.value ?? null}
        name={field.name}
      />
      <InputErrorMessage name={name} />
    </div>
  );
};

const classNames = {
  disabled:
    "disabled:border-slate-200 dark:disabled:border-slate-700 disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:text-slate-400 dark:disabled:text-slate-600 disabled:cursor-not-allowed",
};
